import { Container } from '@bridebook/ui';
import FooterBottom from '@bridebook/web/app-shared/components/footer/footer-bottom/footer-bottom';
import { DesktopPreviewDevice } from '@bridebook/web/lib/wedding-website/desktop/types';
import { useMotifThemeCss } from '@bridebook/web/lib/wedding-website/motif/hooks/use-motif-theme-css';
import { useWeddingWebsiteData } from '../data/data-context';
import { DataProviderGuest } from '../data/data-provider-guest';
import { DataProviderPanel } from '../data/data-provider-panel';
import { MotifCssUnavailable } from './components/motif-css-unavailable';
import { WeddingWebsiteMotif } from './wedding-website-motif';

type PanelProps = {
  dataProvider: 'panel';
  editMode: boolean;
  isSidebarEditor: boolean;
  activePreviewDevice: DesktopPreviewDevice;
};
type GuestProps = {
  dataProvider: 'guest';
  editMode?: never;
  isSidebarEditor?: never;
};

type CommonProps = {
  withFooter?: boolean;
};

type Props = (PanelProps | GuestProps) & CommonProps;

/**
 * Picks the data provider (panel vs guest) and renders the Motif layout. Editor surfaces self-fetch the
 * draft css inline (one shared hook, use-motif-theme-css); the guest keeps a real SSR'd <link> at the
 * published CDN URL. Mirrors WeddingWebsitePrimaryWithData so the Motif layout slots into the same render
 * paths (preview-panel, guest-view, preview-mode, full-page-preview-modal).
 */
export const WeddingWebsiteMotifWithData = (props: Props) => {
  if (props.dataProvider === 'panel') {
    return (
      <DataProviderPanel
        editMode={props.editMode}
        isSidebarEditor={props.isSidebarEditor}
        activePreviewDevice={props.activePreviewDevice}>
        <WeddingWebsitePanelWrapped />
        {props.withFooter && (
          <Container>
            <FooterBottom localeSelect={null} />
          </Container>
        )}
      </DataProviderPanel>
    );
  }

  return (
    <DataProviderGuest>
      <WeddingWebsiteGuestWrapped />
    </DataProviderGuest>
  );
};

const WeddingWebsitePanelWrapped = () => {
  const { cssText, failed } = useMotifThemeCss();

  if (cssText == null) {
    return (
      <div className="w-full" style={{ height: 'var(--ww-canvas-height, 100svh)' }}>
        {failed && <MotifCssUnavailable className="h-full" />}
      </div>
    );
  }

  return (
    <div className="animate-in duration-200 fade-in-0">
      <WeddingWebsiteMotif inlineCss={cssText} withEditorSync />
    </div>
  );
};

const WeddingWebsiteGuestWrapped = () => {
  const { website } = useWeddingWebsiteData();
  return <WeddingWebsiteMotif guest guestCssHash={website.config.motif?.cssHash} />;
};
